import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Box,
  Divider,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { apiService } from "../components/ReasuableFuncations/api";

const RiskDashboard = () => {
  const [risks, setRisks] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [riskToDelete, setRiskToDelete] = useState(null);
  const [editFormData, setEditFormData] = useState({
    description: "",
    type: "",
    probability: "",
    impact: "",
    status: "",
    reviewDate: "",
  });
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      setUserRole({ selectedOption, regionalNetworkOption, memberStateOption, countryOption });
    }
  }, []);

  useEffect(() => {
    const fetchRisks = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData) {
          throw new Error('User data not found');
        }

        const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
        
        let response;
        switch (selectedOption) {
          case 'Comesa':
            response = await apiService.get('/api/risk-fetch');
            break;

          case 'Regional Network':
            if (regionalNetworkOption) {
              response = await apiService.get(`/api/risk-fetch?regionalNetwork=${regionalNetworkOption}`);
            }
            break;

          case 'Member State':
            if (memberStateOption) {
              response = await apiService.get(`/api/risk-fetch?country=${countryOption}`);
            }
            break;

          default:
            throw new Error('Invalid selection');
        }

        if (response.data) {
          setRisks(response.data);
        }
      } catch (error) {
        console.error("Error fetching risks:", error);
        setError("Failed to fetch risks. Please try again later.");
      }
    };

    fetchRisks();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const isOverdue = (lastReviewed) => {
    const reviewDate = new Date(lastReviewed);
    const today = new Date();
    return (today - reviewDate) / (1000 * 60 * 60 * 24) > 30;
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleDeleteConfirmation = (riskId) => {
    setRiskToDelete(riskId);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (riskToDelete) {
      try {
        const response = await apiService.delete(`/api/risks/${riskToDelete}`);
        if (response.data) {
          setRisks((prevRisks) => 
            prevRisks.filter((risk) => risk._id !== riskToDelete)
          );
          setSnackbar({
            open: true,
            message: "Risk deleted successfully!",
            severity: "success",
          });
          setOpenDeleteDialog(false);
          setRiskToDelete(null);
        }
      } catch (error) {
        console.error("Delete error:", error);
        setSnackbar({
          open: true,
          message: error.message || "Error deleting risk",
          severity: "error",
        });
      }
    }
  };

  const handleEdit = (risk) => {
    setEditFormData({
      description: risk.description,
      type: risk.type,
      probability: risk.probability,
      impact: risk.impact,
      status: risk.status,
      reviewDate: risk.reviewDate.split('T')[0],
    });
    setSelectedRisk(risk);
    setOpenDialog(true);
  };

  const handleUpdate = async () => {
    try {
      if (!selectedRisk?._id) {
        throw new Error("No risk selected for update");
      }

      const response = await apiService.put(
        `/api/risks/${selectedRisk._id}`, 
        editFormData
      );

      if (response.data) {
        setRisks(risks.map(risk => 
          risk._id === selectedRisk._id ? 
          { ...risk, ...editFormData } : 
          risk
        ));
        
        setSnackbar({
          open: true,
          message: "Risk updated successfully!",
          severity: "success",
        });
        setOpenDialog(false);
      }
    } catch (error) {
      console.error("Update error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error updating risk",
        severity: "error",
      });
    }
  };

  const canEditDelete = (risk) => {
    if (!userRole) return false;
    
    switch (userRole.selectedOption) {
      case 'Comesa':
        return true; // Comesa users can edit/delete all risks
      case 'Regional Network':
        return risk.organization === userRole.regionalNetworkOption;
      case 'Member State':
        return risk.countryOption === userRole.countryOption;
      default:
        return false;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: "100%", md: "100%" },
        // maxWidth: "1350px",

        margin: "auto",
        p: { xs: 1, sm: 2, md: 3 },
        overflowX: "auto",
      }}
    >
      <div style={{marginRight: "24px" }}>
        <h1
          style={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "center",
          }}
        >
          Risk Dashboard
        </h1>
        <Divider sx={{ marginBottom: 2 }} />

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Project Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Risk Type</TableCell>
                <TableCell>Probability</TableCell>
                <TableCell>Impact</TableCell>
                <TableCell>Risk Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Review Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {risks.map((risk) => (
                <TableRow key={risk._id}>
                  <TableCell>
                    {risk.projectName?.projectName || "N/A"}
                  </TableCell>
                  <TableCell>{risk.description}</TableCell>
                  <TableCell>{risk.type}</TableCell>
                  <TableCell>{risk.probability}</TableCell>
                  <TableCell>{risk.impact}</TableCell>
                  <TableCell>{risk.owner?.name || "N/A"}</TableCell>
                  <TableCell>{risk.status}</TableCell>
                  <TableCell>{formatDate(risk.reviewDate)}</TableCell>
                  <TableCell>
                    {canEditDelete(risk) && (
                      <>
                        <Button
                          size="small"
                          color="success"
                          onClick={() => handleEdit(risk)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteConfirmation(risk._id)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit Risk</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Description"
            value={editFormData.description}
            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Risk Type</InputLabel>
            <Select
              value={editFormData.type}
              onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
            >
              <MenuItem value="Financial">Financial</MenuItem>
              <MenuItem value="Operational">Operational</MenuItem>
              <MenuItem value="Strategic">Strategic</MenuItem>
              <MenuItem value="External">External</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Probability</InputLabel>
            <Select
              value={editFormData.probability}
              onChange={(e) => setEditFormData({ ...editFormData, probability: e.target.value })}
            >
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="High">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Impact</InputLabel>
            <Select
              value={editFormData.impact}
              onChange={(e) => setEditFormData({ ...editFormData, impact: e.target.value })}
            >
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="High">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={editFormData.status}
              onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
            >
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Mitigated">Mitigated</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="date"
            label="Review Date"
            value={editFormData.reviewDate}
            onChange={(e) => setEditFormData({ ...editFormData, reviewDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdate} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this risk?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiskDashboard;
